import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable';
import type { User, Session } from '@supabase/supabase-js';

interface SubscriptionInfo {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
  currentPriceId: string | null;
  recurring: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  /** True if user is admin OR moderator — has access to admin panel */
  isStaff: boolean;
  subscription: SubscriptionInfo;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  checkSubscription: (force?: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CachedValue<T> {
  value: T;
  timestamp: number;
}

function getCache<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const cached: CachedValue<T> = JSON.parse(raw);
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return cached.value;
  } catch {
    return null;
  }
}

function setCache<T>(key: string, value: T) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ value, timestamp: Date.now() }));
  } catch { /* storage full, ignore */ }
}

function clearAuthCache() {
  sessionStorage.removeItem('auth_roles');
  sessionStorage.removeItem('auth_subscription');
  sessionStorage.removeItem('auth_dailyBonus');
}

function hasCompleteSubscriptionCache(value: unknown): value is SubscriptionInfo {
  if (!value || typeof value !== 'object') return false;

  const cached = value as Partial<SubscriptionInfo>;

  if (typeof cached.subscribed !== 'boolean') return false;
  if (typeof cached.productId !== 'string' && cached.productId !== null) return false;
  if (typeof cached.subscriptionEnd !== 'string' && cached.subscriptionEnd !== null) return false;
  if (typeof cached.cancelAtPeriodEnd !== 'boolean') return false;
  if (typeof cached.currentPriceId !== 'string' && cached.currentPriceId !== null) return false;
  if (typeof cached.recurring !== 'boolean') return false;

  if (cached.subscribed && (!cached.currentPriceId || !cached.subscriptionEnd)) {
    return false;
  }

  return true;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Always clear subscription cache on mount (page refresh) to avoid stale data
  sessionStorage.removeItem('auth_subscription');
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionInfo>({
    subscribed: false,
    productId: null,
    subscriptionEnd: null,
    cancelAtPeriodEnd: false,
    currentPriceId: null,
    recurring: true,
  });

  const lastSessionId = useRef<string | null>(null);

  // loading is true until BOTH auth and role check are done
  const loading = authLoading || roleLoading;

  const checkRoles = async (userId: string, force = false) => {
    if (!force) {
      const cached = getCache<{ admin: boolean; moderator: boolean }>('auth_roles');
      if (cached !== null) {
        setIsAdmin(cached.admin);
        setIsModerator(cached.moderator);
        setRoleLoading(false);
        return;
      }
    }

    try {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      const roles = (data || []).map((r) => r.role);
      const admin = roles.includes('admin');
      const moderator = roles.includes('moderator');
      setIsAdmin(admin);
      setIsModerator(moderator);
      setCache('auth_roles', { admin, moderator });
    } catch {
      setIsAdmin(false);
      setIsModerator(false);
    } finally {
      setRoleLoading(false);
    }
  };

  const checkSubscription = async (force = false) => {
    if (!force) {
      const cached = getCache<SubscriptionInfo>('auth_subscription');
      if (hasCompleteSubscriptionCache(cached)) {
        setSubscription(cached);
        return;
      }
      sessionStorage.removeItem('auth_subscription');
    } else {
      sessionStorage.removeItem('auth_subscription');
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) throw error;
      const sub: SubscriptionInfo = {
        subscribed: data?.subscribed ?? false,
        productId: data?.product_id ?? null,
        subscriptionEnd: data?.subscription_end ?? null,
        cancelAtPeriodEnd: data?.cancel_at_period_end ?? false,
        currentPriceId: data?.current_price_id ?? null,
        recurring: data?.recurring ?? true,
      };
      setSubscription(sub);
      setCache('auth_subscription', sub);
    } catch {
      // silently fail - user just isn't subscribed
    }
  };

  const awardDailyLoginBonus = async (_userId: string) => {
    // Skip if already attempted today
    const today = new Date().toISOString().slice(0, 10);
    const cachedDate = getCache<string>('auth_dailyBonus');
    if (cachedDate === today) return;

    const { data } = await supabase.rpc('claim_daily_bonus');
    setCache('auth_dailyBonus', today);
    if (data && (data as any).awarded) {
      const { toast } = await import('sonner');
      toast.success('+10 XP — Daily login bonus! 🎁', { duration: 3000 });
    }
  };

  const handleSession = (session: Session | null) => {
    // Deduplicate: skip if same session
    const sessionId = session?.access_token?.slice(-20) ?? null;
    if (sessionId && sessionId === lastSessionId.current) {
      // Same session, just ensure loading is false
      setAuthLoading(false);
      return;
    }
    lastSessionId.current = sessionId;

    setSession(session);
    setUser(session?.user ?? null);
    setAuthLoading(false);

    if (session?.user) {
      checkRoles(session.user.id);
      checkSubscription();
      awardDailyLoginBonus(session.user.id);
    } else {
      setIsAdmin(false);
      setIsModerator(false);
      setRoleLoading(false);
      setSubscription({ subscribed: false, productId: null, subscriptionEnd: null, cancelAtPeriodEnd: false, currentPriceId: null, recurring: true });
    }
  };

  useEffect(() => {
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((_event, session) => {
      handleSession(session);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session);
    });

    // Re-check subscription when user returns to the tab (covers cancel/purchase in another tab)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && lastSessionId.current) {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session?.user) {
            checkRoles(session.user.id, true);
          }
        });
        checkSubscription(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      authSub.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    clearAuthCache(); // Force fresh checks on new sign-in
    lastSessionId.current = null;
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    clearAuthCache();
    lastSessionId.current = null;
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const isLovableHost = /(?:\.lovable\.app|\.lovableproject\.com)$/i.test(window.location.hostname) || window.location.hostname === 'dev.mnemospeak.com';

  const signInWithGoogle = async () => {
    clearAuthCache();
    lastSessionId.current = null;
    if (isLovableHost) {
      const result = await lovable.auth.signInWithOAuth('google', {
        redirect_uri: window.location.origin,
      });
      if (result.error) throw result.error;
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin },
      });
      if (error) throw error;
    }
  };

  const signInWithApple = async () => {
    clearAuthCache();
    lastSessionId.current = null;
    if (isLovableHost) {
      const result = await lovable.auth.signInWithOAuth('apple', {
        redirect_uri: window.location.origin,
      });
      if (result.error) throw result.error;
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: { redirectTo: window.location.origin },
      });
      if (error) throw error;
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  const isStaff = isAdmin || isModerator;

  return (
    <AuthContext.Provider value={{
      user, session, loading, isAdmin, isModerator, isStaff, subscription,
      signIn, signUp, signOut, signInWithGoogle, signInWithApple,
      resetPassword, checkSubscription,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
